import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { TestExecutionWithDetails } from '../types';
import { parseJiraTickets } from '../utils/formatters';
import './TestExecutionModal.css';

interface Evidence {
  id: string;
  execution_id: string;
  file_path: string;
  file_name: string;
  display_order: number;
  uploaded_at: string;
  uploaded_by: string;
}

interface FileWithPreview extends File {
  preview?: string;
}

interface EvidenceWithPreview extends Evidence {
  previewUrl?: string;
}

interface Props {
  execution: TestExecutionWithDetails;
  onClose: () => void;
  onComplete: () => void;
}

const TestExecutionModal: React.FC<Props> = ({ execution, onClose, onComplete }) => {
  const [status, setStatus] = useState<string>(execution.execution_status === 'NOT_RUN' ? 'PASS' : execution.execution_status);
  const [comment, setComment] = useState<string>(execution.comment || '');
  const [defectReference, setDefectReference] = useState<string>(execution.defect_reference || '');
  const [evidenceFiles, setEvidenceFiles] = useState<FileWithPreview[]>([]);
  const [existingEvidence, setExistingEvidence] = useState<EvidenceWithPreview[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Load existing evidence when modal opens
  useEffect(() => {
    const loadEvidence = async () => {
      try {
        setLoadingEvidence(true);
        const evidence = await api.getExecutionEvidence(execution.id);
        
        // Load preview URLs for existing evidence
        const evidenceWithPreviews = await Promise.all(
          (evidence || []).map(async (ev) => {
            try {
              // Fetch the file with authentication
              const response = await fetch(api.getEvidenceDownloadUrl(ev.id), {
                headers: {
                  'Authorization': `Bearer ${JSON.parse(localStorage.getItem('auth_tokens') || '{}').access_token}`
                }
              });
              
              if (response.ok) {
                const blob = await response.blob();
                const previewUrl = URL.createObjectURL(blob);
                return { ...ev, previewUrl };
              }
            } catch (err) {
              console.error('Failed to load preview for:', ev.file_name, err);
            }
            return ev;
          })
        );
        
        setExistingEvidence(evidenceWithPreviews);
      } catch (err) {
        console.error('Failed to load evidence:', err);
      } finally {
        setLoadingEvidence(false);
      }
    };

    loadEvidence();
  }, [execution.id]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      evidenceFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      existingEvidence.forEach(ev => {
        if (ev.previewUrl) {
          URL.revokeObjectURL(ev.previewUrl);
        }
      });
    };
  }, [evidenceFiles, existingEvidence]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      addFilesToEvidence(files);
    }
  };

  const addFilesToEvidence = (files: File[]) => {
    // Validate file types (images and videos)
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return isImage || isVideo;
    });

    if (validFiles.length === 0) {
      setFileError('Only image and video files are allowed');
      return;
    }

    // Validate file size (max 200MB per file)
    const maxSize = 200 * 1024 * 1024;
    const oversizedFiles = validFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setFileError('File size must be less than 200MB');
      return;
    }

    const filesWithPreview: FileWithPreview[] = validFiles.map(file => {
      const fileWithPreview = file as FileWithPreview;
      fileWithPreview.preview = URL.createObjectURL(file);
      return fileWithPreview;
    });

    setEvidenceFiles(prev => [...prev, ...filesWithPreview]);
    setFileError(null); // clear any previous file validation error
    setError(null);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const files = imageItems
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null)
      .map((f, i) => {
        // Give pasted images a meaningful name with timestamp
        const ext = f.type.split('/')[1] || 'png';
        return new File([f], `screenshot_${Date.now()}_${i + 1}.${ext}`, { type: f.type });
      });
    addFilesToEvidence(files);
  };

  const removeFile = (index: number) => {
    setEvidenceFiles(prev => {
      const fileToRemove = prev[index];
      if (fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
    setFileError(null); // removing a file clears any file validation error
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    
    if (ext && imageExts.includes(ext)) return '🖼️';
    if (ext && videoExts.includes(ext)) return '🎥';
    return '📄';
  };

  const handleDownloadEvidence = async (evidenceId: string, fileName: string) => {
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const token = authTokens ? JSON.parse(authTokens).access_token : '';

      // Get the presigned URL or local download URL
      try {
        const urlInfo = await api.getEvidenceUrl(evidenceId);
        if (urlInfo.storage === 's3' && urlInfo.url) {
          // S3: open presigned URL directly — browser handles download natively
          const a = document.createElement('a');
          a.href = urlInfo.url;
          a.download = fileName;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
      } catch {
        // fall through to local download
      }

      // Local: fetch with auth header and trigger download via blob
      const response = await fetch(api.getEvidenceDownloadUrl(evidenceId), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Update execution status first
      await api.updateExecution(execution.id, {
        execution_status: status,
        comment: comment || undefined,
        defect_reference: defectReference || undefined
      });
      
      // Upload evidence files if any
      if (evidenceFiles.length > 0) {
        for (let i = 0; i < evidenceFiles.length; i++) {
          await api.uploadExecutionEvidence(execution.id, evidenceFiles[i], i);
        }
      }
      
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to update execution');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Execute Test Case</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="test-case-details">
              <div className="detail-section">
                <h3>Test Case Description</h3>
                <div
                  className="rich-text-display"
                  dangerouslySetInnerHTML={{ __html: execution.test_case_description || '' }}
                />
              </div>

              {execution.test_case_preconditions && (
                <div className="detail-section">
                  <h3>Preconditions</h3>
                  <div
                    className="rich-text-display"
                    dangerouslySetInnerHTML={{ __html: execution.test_case_preconditions }}
                  />
                </div>
              )}

              <div className="detail-section">
                <h3>Expected Result</h3>
                <div
                  className="rich-text-display"
                  dangerouslySetInnerHTML={{ __html: execution.test_case_expected_result || '' }}
                />
              </div>
            </div>

            <div className="execution-form">
              <div className="form-group">
                <label htmlFor="status">Execution Status *</label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  required
                >
                  <option value="PASS">Pass</option>
                  <option value="FAIL">Fail</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="comment">Comments</label>
                <textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="Add execution notes, observations, or issues..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="defectReference">Defect / Note</label>
                <input
                  type="text"
                  id="defectReference"
                  value={defectReference}
                  onChange={(e) => setDefectReference(e.target.value)}
                  placeholder="e.g., PLANNER-545 or Evidence same as TS-00874"
                />
                {defectReference.trim() ? (
                  <div className="defect-preview">
                    {parseJiraTickets(defectReference).map((seg, i) =>
                      typeof seg === 'string'
                        ? <React.Fragment key={i}>{seg}</React.Fragment>
                        : <a
                            key={i}
                            href={seg.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="defect-jira-link"
                            title={`Open ${seg.ticket} in Jira`}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 3 }}>
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            {seg.ticket}
                          </a>
                    )}
                  </div>
                ) : (
                  <small>Enter a Jira ticket (e.g. PLANNER-545) to auto-link it, or any note</small>
                )}
              </div>

              {/* Display existing evidence */}
              {existingEvidence.length > 0 && (
                <div className="form-group">
                  <label>Previously Uploaded Evidence ({existingEvidence.length})</label>
                  <div className="existing-evidence">
                    <ul className="evidence-list">
                      {existingEvidence.map((evidence) => (
                        <li key={evidence.id} className="evidence-item existing with-preview">
                          <div className="file-preview-container">
                            {evidence.previewUrl && getFileIcon(evidence.file_name) === '🖼️' ? (
                              <img 
                                src={evidence.previewUrl} 
                                alt={evidence.file_name}
                                className="file-preview-image"
                              />
                            ) : evidence.previewUrl && getFileIcon(evidence.file_name) === '🎥' ? (
                              <video 
                                src={evidence.previewUrl}
                                className="file-preview-video"
                                controls
                              />
                            ) : (
                              <div className="file-preview-placeholder">
                                <span className="file-icon-large">{getFileIcon(evidence.file_name)}</span>
                                <div style={{fontSize: '12px', marginTop: '10px', color: '#666'}}>
                                  {loadingEvidence ? 'Loading preview...' : 'Preview not available'}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="file-info">
                            <span className="file-icon">
                              {getFileIcon(evidence.file_name)}
                            </span>
                            <span className="file-name">{evidence.file_name}</span>
                            <span className="file-meta">
                              Uploaded by {evidence.uploaded_by} on {new Date(evidence.uploaded_at).toLocaleString()}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-download"
                            onClick={() => handleDownloadEvidence(evidence.id, evidence.file_name)}
                            title="Download file"
                          >
                            ⬇️
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="evidence">Upload New Evidence (Images/Videos)</label>
                <input
                  type="file"
                  id="evidence"
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                  multiple
                  className="file-input"
                />
                {/* Clipboard paste zone */}
                <div
                  className="paste-zone"
                  onPaste={handlePaste}
                  tabIndex={0}
                  title="Click here then press Ctrl+V / Cmd+V to paste a screenshot"
                >
                  <span className="paste-zone-icon">📋</span>
                  <span className="paste-zone-text">
                    Click here &amp; press <kbd>Ctrl+V</kbd> to paste a screenshot from clipboard
                  </span>
                </div>
                {fileError && (
                  <div className="file-error-message">
                    ⚠️ {fileError} — please remove the invalid file before saving.
                  </div>
                )}
                <small>Upload screenshots or screen recordings as evidence (max 200MB per file)</small>
                
                {evidenceFiles.length > 0 && (
                  <div className="evidence-preview">
                    <h4>New Files to Upload ({evidenceFiles.length})</h4>
                    <ul className="evidence-list">
                      {evidenceFiles.map((file, index) => (
                        <li key={index} className="evidence-item with-preview">
                          <div className="file-preview-container">
                            {file.type.startsWith('image/') ? (
                              <img 
                                src={file.preview} 
                                alt={file.name}
                                className="file-preview-image"
                              />
                            ) : file.type.startsWith('video/') ? (
                              <video 
                                src={file.preview}
                                className="file-preview-video"
                                controls
                              />
                            ) : null}
                          </div>
                          <div className="file-info">
                            <span className="file-icon">
                              {file.type.startsWith('image/') ? '🖼️' : '🎥'}
                            </span>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-remove-file"
                            onClick={() => removeFile(index)}
                            title="Remove file"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving || !!fileError}>
              {saving ? 'Saving...' : 'Save Execution'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestExecutionModal;
